const CONFIG = {
    namespace: 'duel-analytics',
    storageMinutesToLive: 43200, // 30 days
    affiliateAttributionCap: Number.MAX_SAFE_INTEGER,
    get storageTTL() {
        return this.storageMinutesToLive * 60 * 1000;
    }
};

const StorageHelper = {
    /**
     * Checks if localStorage is available and working
     */
    isLocalStorageSupported() {
        try {
            const key = `${CONFIG.namespace}-test`;
            window.localStorage.setItem(key, '{}');
            window.localStorage.removeItem(key);
            return true;
        } catch (e) {
            return false;
        }
    },

    /**
     * Fallback Cookie Storage
     */
    cookie: {
        save(value) {
            const isSecure = location.protocol === 'https:';
            const expires = new Date(Date.now() + CONFIG.storageTTL).toUTCString();
            let cookieStr = `${CONFIG.namespace}=${encodeURIComponent(value)}; path=/; expires=${expires}`;

            if (isSecure) cookieStr += '; Secure; SameSite=None';
            document.cookie = cookieStr;
        },
        retrieve() {
            const cookies = document.cookie.split(/\s*;\s*/);
            const target = cookies.find(row => row.startsWith(`${CONFIG.namespace}=`));
            if (!target) return null;
            return decodeURIComponent(target.split('=')[1]);
        }
    }
};

const ParametersManager = {
    // Defined parameters to watch for in URL
    definitions: [
        { name: 'duel_a', sendAs: 'affiliate' },
        { name: 'duel_c', sendAs: 'ugc' }
    ],

    /**
     * Scans the current URL for defined parameters and saves them
     */
    captureFromURL() {
        const url = new URL(location.href);
        const captured = {};

        this.definitions.forEach(def => {
            if (url.searchParams.has(def.name)) {
                const val = url.searchParams.get(def.name);
                if (val) {
                    captured[def.name] = val;
                }
                // Optional: clean up the URL after capture
                url.searchParams.delete(def.name);
            }
        });

        if (Object.keys(captured).length > 0) {
            this.save(captured);
            // Update browser history to hide the parameters
            if (window.history.replaceState) {
                window.history.replaceState(null, document.title, url.toString());
            }
        }
    },

    /**
     * Merges new parameters with existing storage
     */
    save(newParams) {
        const currentData = this.retrieve();

        for (const key in newParams) {
            currentData[key] = {
                value: newParams[key],
                time: Date.now(),
                usage: newParams[key] === currentData[key] ? (currentData[key]?.usage || 0) + 1 : 1
            };
        }

        const serialized = JSON.stringify(currentData);
        if (StorageHelper.isLocalStorageSupported()) {
            localStorage.setItem(CONFIG.namespace, serialized);
        }
        StorageHelper.cookie.save(serialized);
    },

    /**
     * Retrieves and prunes (cleans) stored parameters
     */
    retrieve() {
        let rawData = null;

        if (StorageHelper.isLocalStorageSupported()) {
            rawData = localStorage.getItem(CONFIG.namespace);
        }
        if (!rawData) {
            rawData = StorageHelper.cookie.retrieve();
        }

        let data = {};
        try {
            data = rawData ? JSON.parse(rawData) : {};
        } catch (e) {
            data = {};
        }

        return this.prune(data);
    },

    /**
     * Removes expired data or data that has hit the usage cap
     */
    prune(data) {
        const expiryThreshold = Date.now() - CONFIG.storageTTL;

        for (const key in data) {
            const item = data[key];
            let reason = null;

            if (item.time < expiryThreshold) {
                reason = 'expired';
            } else if (key === 'duel_a' && (item.usage || 0) >= CONFIG.affiliateAttributionCap) {
                reason = 'capped';
            }

            if (reason) {
                delete data[key];
            }
        }

        return data;
    }
};

// Initialize
ParametersManager.captureFromURL();
